import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import Button from "../wrappers/Button";
import Checkbox from "../wrappers/Checkbox";
import FormControlLabel from "../wrappers/FormControlLabel";
import FormHelperText from "../wrappers/FormHelperText";
import FormLabel from "../wrappers/FormLabel";
import Typography from "../wrappers/Typography";
import SearchFilterOptionsDialog from "./SearchFilterOptionsDialog";
import { includesValue, isSameValue } from "./SearchFilters.helpers";

/**
 * Renderiza uma seleção múltipla controlada e imediatamente publicada.
 *
 * @param {object} pProps Propriedades do componente.
 * @param {string} pProps.id Identificador estável do filtro.
 * @param {string} pProps.label Rótulo acessível do grupo.
 * @param {Array<*>} pProps.value Lista controlada pelo consumidor.
 * @param {Array<object>} pProps.options Opções na ordem desejada pelo consumidor.
 * @param {Function} pProps.onChange Recebe a lista completa na ordem das opções.
 * @param {boolean} [pProps.disabled=false] Desabilita o controle.
 * @param {React.ReactNode} [pProps.helperText] Explicação complementar da seleção.
 * @param {number} [pProps.visibleOptionCount] Quantidade visível antes do diálogo.
 * @param {string} [pProps.optionsDialogTitle] Título do diálogo de opções extensas.
 * @returns {React.ReactElement}
 */
const SearchFilterMultiSelect = (pProps) => {
  const {
    id,
    label,
    value,
    options,
    onChange,
    disabled = false,
    helperText,
    visibleOptionCount,
    optionsDialogTitle,
  } = pProps;
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const xHasOptionsDialog = visibleOptionCount !== undefined && options.length > visibleOptionCount;
  const xVisibleOptions = xHasOptionsDialog ? options.slice(0, visibleOptionCount) : options;
  const xLabelId = `${id}-label`;
  const xHelperTextId = helperText ? `${id}-helper-text` : undefined;

  /** Publica a nova seleção já ordenada conforme as opções do consumidor. */
  const pvToggleOption = (pOption) => {
    if (disabled || pOption.disabled) {
      return;
    }

    const xNextValue = includesValue(value, pOption.value)
      ? options
          .filter(
            (pItem) =>
              includesValue(value, pItem.value) && !isSameValue(pItem.value, pOption.value),
          )
          .map((pItem) => pItem.value)
      : options
          .filter(
            (pItem) => includesValue(value, pItem.value) || isSameValue(pItem.value, pOption.value),
          )
          .map((pItem) => pItem.value);

    onChange(xNextValue);
  };

  return (
    <Box
      role="group"
      aria-label={label}
      aria-labelledby={xLabelId}
      aria-describedby={xHelperTextId}
      sx={{ display: "flex", flexDirection: "column" }}
    >
      <FormLabel disabled={disabled} id={xLabelId} sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={500}>
          {label}
        </Typography>
      </FormLabel>
      {helperText ? <FormHelperText id={xHelperTextId}>{helperText}</FormHelperText> : null}
      {xVisibleOptions.map((pOption) => (
        <FormControlLabel
          key={pOption.id}
          control={
            <Checkbox
              checked={includesValue(value, pOption.value)}
              onChange={() => pvToggleOption(pOption)}
              size="small"
            />
          }
          disabled={disabled || pOption.disabled}
          label={<Typography variant="caption">{pOption.label}</Typography>}
        />
      ))}
      {xHasOptionsDialog ? (
        <Button
          disabled={disabled}
          onClick={() => setOptionsDialogOpen(true)}
          type="button"
          variant="text"
          size="small"
        >
          Ver todas
        </Button>
      ) : null}
      {xHasOptionsDialog ? (
        <SearchFilterOptionsDialog
          id={`${id}-options`}
          onClose={() => setOptionsDialogOpen(false)}
          onConfirm={(pValue) => {
            onChange(pValue);
            setOptionsDialogOpen(false);
          }}
          open={optionsDialogOpen}
          options={options}
          title={optionsDialogTitle || label}
          value={value}
        />
      ) : null}
    </Box>
  );
};

SearchFilterMultiSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.arrayOf(PropTypes.any).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      disabled: PropTypes.bool,
      neutral: PropTypes.bool,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  helperText: PropTypes.node,
  visibleOptionCount: PropTypes.number,
  optionsDialogTitle: PropTypes.string,
};

export default SearchFilterMultiSelect;
